import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuction } from '../../context/AuctionContext';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, orderBy, doc, onSnapshot, where, addDoc, serverTimestamp, limit, updateDoc, arrayUnion, arrayRemove, setDoc } from 'firebase/firestore';
import { seasonPlayersCollection, seasonClubsCollection } from '../../services/players';
import { fetchSeasons } from '../../services/seasons';
import { isActiveSeasonPlayer, isScoutingLocked, scoutingLimit, REFUNDED_TRANSFER_TYPES, NOT_ELIGIBLE_TRANSFER_TYPE } from '../../constants/game';
import { useNotification } from '../../context/NotificationContext';
import { db, functions } from '../../firebase';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Avatar,
    TextField,
    Grid,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip,
    Checkbox,
    FormControlLabel,
    IconButton,
    Drawer,
    Badge,
    Switch,
    Divider,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { sortPlayers } from '../../utils/sorting';
import TransferConfirmationModal from '../../components/TransferConfirmationModal';
import TransfersModal from '../../components/TransfersModal';
import BlindPoolBidModal from '../../components/BlindPoolBidModal';
import DraftBoard from '../../components/DraftBoard';
import DraftPlayersTable from '../../components/DraftPlayersTable';
import PlayerCard from '../../components/PlayerCard';
import PlayerDetailModal from '../../components/PlayerDetailModal';
import SidelinedIcon from '../../components/SidelinedIcon';
import { useSidelinedMap } from '../../hooks/useSidelinedMap';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import GetAppIcon from '@mui/icons-material/GetApp';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import { containers, text, page } from '../../theme/commonStyles';


// --- Team Validation Logic (copied from AdminAuction) ---
const POSITION_MAP = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Coach'
};

// Sorteeropties voor de lijst met vrije spelers. 'club' is de standaard (de lijst
// wordt al op club/positie/naam gesorteerd binnengehaald); de puntenopties gebruiken
// de per-seizoen totalen uit playerScores/{seasonId}/totals.
const SORT_OPTIONS = [
    { value: 'club', label: 'Club' },
    { value: 'currentPoints', label: 'Punten (huidig seizoen)' },
    { value: 'prevPoints', label: 'Punten (vorig seizoen)' },
];

const TEAM_RULES = {
    'Totaal': { min: 12, max: 12 },
    'Coach': { min: 1, max: 1 },
    'Keepers': { min: 1, max: 1 },
    'Verdedigers': { min: 3, max: 5 },
    'Middenvelders': { min: 3, max: 5 },
    'Aanvallers': { min: 2, max: 4 },
};

const getRuleKeyForPosition = (dutchPosition) => {
    if (dutchPosition === 'Coach') return 'Coach';
    if (dutchPosition === 'Keeper') return 'Keepers';
    if (dutchPosition) return dutchPosition + 's';
    return null;
}

const validateTeamComposition = (teamPlayers, playerToAdd) => {
    const newTeamSize = teamPlayers.length + 1;
    if (newTeamSize > TEAM_RULES['Totaal'].max) {
        return `Je team is vol (${TEAM_RULES['Totaal'].max} spelers).`;
    }

    const composition = teamPlayers.reduce((acc, player) => {
        const dutchPosition = POSITION_MAP[player.position] || player.position;
        acc[dutchPosition] = (acc[dutchPosition] || 0) + 1;
        return acc;
    }, {});

    const newPlayerDutchPosition = POSITION_MAP[playerToAdd.position] || playerToAdd.position;
    const ruleKeyForNewPlayer = getRuleKeyForPosition(newPlayerDutchPosition);

    if (ruleKeyForNewPlayer && TEAM_RULES[ruleKeyForNewPlayer]) {
        const currentCount = composition[newPlayerDutchPosition] || 0;
        const maxCount = TEAM_RULES[ruleKeyForNewPlayer].max;
        if (currentCount >= maxCount) {
            return `Maximum aantal ${ruleKeyForNewPlayer.toLowerCase()} (${maxCount}) bereikt.`;
        }
    }

    const compositionAfterAdding = { ...composition };
    compositionAfterAdding[newPlayerDutchPosition] = (compositionAfterAdding[newPlayerDutchPosition] || 0) + 1;

    const remainingSlots = TEAM_RULES['Totaal'].max - newTeamSize;
    let stillNeeded = 0;
    for (const ruleKey in TEAM_RULES) {
        if (ruleKey === 'Totaal') continue;

        const positionName = ruleKey === 'Coach' ? 'Coach' : (ruleKey === 'Keepers' ? 'Keeper' : ruleKey.slice(0, -1));
        const min = TEAM_RULES[ruleKey].min;
        const current = compositionAfterAdding[positionName] || 0;

        if (current < min) {
            stillNeeded += (min - current);
        }
    }

    if (stillNeeded > remainingSlots) {
        return `Niet genoeg plekken over om aan de minimale teamvereisten te voldoen.`;
    }

    return null; // Validation passes
};
// --- End of Validation Logic ---

// Compacte zoekbalk (klein, met vergrootglas-icoon en placeholder). Gedeeld door de
// Vrije Spelers-, Alle Transfers- en Mijn Transfers-tab zodat de stijl gelijk blijft.
const CompactSearchField = ({ value, onChange, placeholder }) => (
    <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
            ),
        }}
    />
);

const FreeAgentsTable = ({ players, onAcquire, onBid, participantDetails, auction, lastPlayerElementRef, myBids, validationMessages, onToggleScout, scoutingList, isRoundRunning, isMobile, theme, activeTransferRound, computedTransfersLeft, sidelinedMap, onPlayerClick, pointsMap, pointsSeasonLabel }) => {
    const isBlindPoolPhase = auction?.spelfase === 'Blinde Pool';
    const isDraftPhase = auction?.spelfase === 'Draft';
    // Tijdens de Veiling ligt de scoutlijst vast; de scoutknop is dan uitgeschakeld.
    const scoutingLocked = isScoutingLocked(auction?.spelfase);

    // Bereken transfer round status voor deze deelnemer
    const participantUid = participantDetails?.uid;
    const inTransferRound = activeTransferRound && participantUid && activeTransferRound.participants?.[participantUid];
    const transferRoundData = inTransferRound ? activeTransferRound.participants[participantUid] : null;
    const transferRoundTransfersDone = transferRoundData
        ? Math.max(0, transferRoundData.transfersLeftAtStart - computedTransfersLeft)
        : 0;
    const transferRoundRemaining = transferRoundData ? Math.max(0, transferRoundData.allowance - transferRoundTransfersDone) : 0;

    // Als er een actieve transferronde is, overschrijf de logica:
    // - In de ronde en nog mogelijkheden: enabled
    // - In de ronde maar geen mogelijkheden meer: disabled
    // - Niet in de ronde: disabled (transfermarkt is gesloten)
    let transfersDisabled;
    if (activeTransferRound) {
        if (inTransferRound) {
            transfersDisabled = transferRoundRemaining <= 0 ||
                ['Voorbereiding', 'Veiling', 'Draft'].includes(auction?.spelfase) ||
                isRoundRunning;
        } else {
            transfersDisabled = true;
        }
    } else {
        transfersDisabled = computedTransfersLeft <= 0 ||
            ['Voorbereiding', 'Veiling', 'Draft'].includes(auction?.spelfase) ||
            isRoundRunning || auction?.transfermarketClosed === true;
    }

    const transfersLeft = computedTransfersLeft;

    if (isMobile) {
        return (
            <Box sx={containers.mobileCardList}>
                {players.map((player, index) => {
                    const alreadyBidOn = myBids?.some(b => b.playerId === player.id);
                    const bidsPlaced = myBids?.length || 0;
                    const blindPoolBidsDisabled = bidsPlaced >= transfersLeft;
                    const validationMessage = validationMessages.get(player.id);
                    const isDisabled = !isBlindPoolPhase || blindPoolBidsDisabled || alreadyBidOn || !!validationMessage;
                    const isScouted = scoutingList.includes(player.id);

                    let buttonText = 'Blinde Pool Bieding';
                    if (alreadyBidOn) buttonText = 'Bod Geplaatst';

                    let tooltipText = validationMessage || '';
                    if (blindPoolBidsDisabled && !alreadyBidOn) tooltipText = 'Geen transfers meer over om te bieden.';
                    if (alreadyBidOn) tooltipText = 'Je hebt al een bod op deze speler.';

                    const blindPoolButton = (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => onBid(player)}
                            disabled={isDisabled}
                            fullWidth
                        >
                            {buttonText}
                        </Button>
                    );

                    const acquireButton = (
                        <IconButton color="primary" onClick={() => onAcquire(player)} disabled={transfersDisabled}>
                            <Tooltip title={
                                isDraftPhase ? "Transfers zijn niet mogelijk tijdens de draft fase." :
                                isRoundRunning ? "Transfers zijn niet mogelijk tijdens een lopende speelronde." :
                                auction?.transfermarketClosed ? "De transfermarkt is momenteel gesloten door de beheerder." : "Aantrekken"
                            }>
                                <GetAppIcon />
                            </Tooltip>

                        </IconButton>
                    );

                    return (
                        <PlayerCard
                            key={player.id}
                            ref={players.length === index + 1 ? lastPlayerElementRef : null}
                            player={player}
                            status={sidelinedMap?.get(String(player.id)) || null}
                            onClick={onPlayerClick ? () => onPlayerClick(player) : undefined}
                            subCaption={pointsSeasonLabel ? (
                                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, display: 'block', lineHeight: 1.3 }}>
                                    {pointsMap?.[String(player.id)]?.totalPoints ?? 0} punten · {pointsSeasonLabel}
                                </Typography>
                            ) : undefined}
                            rightSlot={
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                                    <IconButton onClick={() => onToggleScout(player.id)} disabled={scoutingLocked} color={isScouted ? 'secondary' : 'default'}>
                                        {isScouted ? <StarIcon /> : <StarBorderIcon />}
                                    </IconButton>
                                    {!isBlindPoolPhase && (
                                        <span>{acquireButton}</span> // The IconButton already has a Tooltip
                                    )}
                                </Box>
                            }
                            footer={isBlindPoolPhase && (
                                <Box sx={{ p: 1.5, pt: 0, display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                                    {isDisabled && tooltipText ? (
                                        <Tooltip title={tooltipText} arrow sx={{ width: '100%' }}>
                                            <span>{blindPoolButton}</span>
                                        </Tooltip>
                                    ) : (blindPoolButton)}
                                </Box>
                            )}
                        />
                    );
                })}
            </Box>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table aria-label="vrije spelers tabel">
                <TableHead>
                    <TableRow>
                        <TableCell>Speler</TableCell>
                        <TableCell>Positie</TableCell>
                        <TableCell>Club</TableCell>
                        {pointsSeasonLabel ? <TableCell align="right">Punten ({pointsSeasonLabel})</TableCell> : null}
                        <TableCell align="center">Scouten</TableCell>
                        <TableCell align="right">Actie</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {players.map((player, index) => {
                        const alreadyBidOn = myBids?.some(b => b.playerId === player.id);
                        const bidsPlaced = myBids?.length || 0;
                        const blindPoolBidsDisabled = bidsPlaced >= transfersLeft;
                        const validationMessage = validationMessages.get(player.id);
                        const isDisabled = !isBlindPoolPhase || blindPoolBidsDisabled || alreadyBidOn || !!validationMessage;
                        const isScouted = scoutingList.includes(player.id);

                        let buttonText = 'Blinde Pool Bieding';
                        if (alreadyBidOn) buttonText = 'Bod Geplaatst';

                        let tooltipText = validationMessage || '';
                        if (blindPoolBidsDisabled && !alreadyBidOn) tooltipText = 'Geen transfers meer over om te bieden.';
                        if (alreadyBidOn) tooltipText = 'Je hebt al een bod op deze speler.';

                        const button = (
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={() => onBid(player)}
                                disabled={isDisabled}
                            >
                                {buttonText}
                            </Button>
                        );

                        const acquireButton = (
                            <Button variant="contained" color="primary" size="small" onClick={() => onAcquire(player)} disabled={transfersDisabled}>
                                Aantrekken
                            </Button>
                        );

                        return (
                            <TableRow
                                key={player.id}
                                hover
                                ref={players.length === index + 1 ? lastPlayerElementRef : null}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    onClick={onPlayerClick ? () => onPlayerClick(player) : undefined}
                                    sx={onPlayerClick ? { cursor: 'pointer' } : undefined}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar src={player.photoUrl} alt={player.name} />
                                        {player.name}
                                        <SidelinedIcon status={sidelinedMap?.get(String(player.id)) || null} />
                                    </Box>
                                </TableCell>
                                <TableCell>{POSITION_MAP[player.position] || player.position}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {player.clubLogo && <Box component="img" src={player.clubLogo} alt={`${player.clubName} logo`} sx={{ height: 24, width: 24, objectFit: 'contain' }} />}
                                        {player.clubName}
                                    </Box>
                                </TableCell>
                                {pointsSeasonLabel ? (
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {pointsMap?.[String(player.id)]?.totalPoints ?? 0}
                                    </TableCell>
                                ) : null}
                                <TableCell align="center">
                                    <IconButton onClick={() => onToggleScout(player.id)} disabled={scoutingLocked} color={isScouted ? 'secondary' : 'default'}>
                                        {isScouted ? <StarIcon /> : <StarBorderIcon />}
                                    </IconButton>
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {isBlindPoolPhase ? (
                                        isDisabled && tooltipText ? (
                                            <Tooltip title={tooltipText} arrow>
                                                <span>{button}</span>
                                            </Tooltip>
                                        ) : (button)
                                    ) : (
                                        <Tooltip title={
                                            isDraftPhase ? "Transfers zijn niet mogelijk tijdens de draft fase." :
                                            isRoundRunning ? "Transfers zijn niet mogelijk tijdens een lopende speelronde." :
                                            auction?.transfermarketClosed ? "De transfermarkt is momenteel gesloten door de beheerder." :
                                            transfersDisabled ? "Geen transfers meer over." : ""
                                        }>
                                            <span>
                                                {acquireButton}
                                            </span>
                                        </Tooltip>                                        
                                    )}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const TransfersTable = ({ transfers, participantsMap, playersMap, isMobile, lastTransferElementRef, sidelinedMap }) => {

    const getPhaseLabel = (type) => {
        switch (type) {
            case 'auction': return 'Veiling';
            case 'transfer': return 'Transfer';
            case 'blindPoolAssignment': return 'Blinde Pool';
            case 'Transfermarkt': return 'Transfermarkt';
            case 'Draft': return 'Draft';
            case 'Uitschakeling tussenronde': return 'Uitschakeling';
            case NOT_ELIGIBLE_TRANSFER_TYPE: return 'Niet speelgerechtigd';
            default: return type || 'Onbekend';
        }
    };

    if (isMobile) {
        return (
            <Box sx={containers.mobileCardList}>
                {transfers.map((transfer, index) => {
                    const player = playersMap.get(String(transfer.playerId));
                    const fromTeamName = participantsMap.get(transfer.from) || transfer.from;
                    const toTeamName = participantsMap.get(transfer.to) || transfer.to;
                    const isParticipantInvolvedInTo = participantsMap.has(transfer.to);
                    const isParticipantInvolvedInFrom = participantsMap.has(transfer.from);

                    let statusIcon = null;
                    let teamName = '';

                    if (isParticipantInvolvedInTo) {
                        teamName = toTeamName;
                        statusIcon = <ArrowDownwardIcon sx={{ color: 'success.main' }} />;
                    } else if (isParticipantInvolvedInFrom) {
                        teamName = fromTeamName;
                        statusIcon = <ArrowUpwardIcon sx={{ color: 'error.main' }} />;
                    }

                    const date = transfer.timestamp ? new Date(transfer.timestamp.seconds * 1000) : null;
                    const formattedDateTime = date ? date.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

                    return (
                        <PlayerCard
                            key={transfer.id}
                            ref={transfers.length === index + 1 ? lastTransferElementRef : null}
                            player={{
                                name: transfer.playerName,
                                photoUrl: player?.photoUrl,
                                clubLogo: player?.clubLogo,
                                clubName: player?.clubName || 'N/A',
                                position: player?.position,
                            }}
                            status={sidelinedMap?.get(String(transfer.playerId)) || null}
                            leadingSlot={statusIcon}
                            rightSlot={
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="body1" sx={text.bold}>€{transfer.amount}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {teamName}
                                    </Typography>
                                </Box>
                            }
                            footer={
                                <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={text.captionSmall}>
                                        {formattedDateTime}
                                    </Typography>
                                    <Typography variant="caption" sx={text.captionSmall}>
                                        {getPhaseLabel(transfer.type)}
                                    </Typography>
                                </Box>
                            }
                        />
                    );
                })}
            </Box>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table aria-label="transfers tabel">
                <TableHead>
                    <TableRow>
                        <TableCell>Datum</TableCell>
                        <TableCell>Speler</TableCell>
                        <TableCell>Positie</TableCell>
                        <TableCell>Club</TableCell>
                        <TableCell align="right">Bedrag</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {transfers.map((transfer, index) => {
                        const player = playersMap.get(String(transfer.playerId));
                        const fromTeamName = participantsMap.get(transfer.from) || transfer.from;
                        const toTeamName = participantsMap.get(transfer.to) || transfer.to;

                        const isParticipantInvolvedInTo = participantsMap.has(transfer.to);
                        const isParticipantInvolvedInFrom = participantsMap.has(transfer.from);

                        let statusChip = null;
                        let teamName = '';

                        if (isParticipantInvolvedInTo) { // Transfer into a team
                            teamName = toTeamName;
                            statusChip = <Chip label="Inkomend" color="success" size="small" variant="outlined" />;
                        } else if (isParticipantInvolvedInFrom) { // Transfer out of a team
                            teamName = fromTeamName;
                            statusChip = <Chip label="Uitgaand" color="error" size="small" variant="outlined" />;
                        } else {
                            // Fallback for transfers between non-participant entities
                            teamName = `${fromTeamName} → ${toTeamName}`;
                        }

                        return (
                            <TableRow key={transfer.id} hover ref={transfers.length === index + 1 ? lastTransferElementRef : null}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {transfer.timestamp ? new Date(transfer.timestamp.seconds * 1000).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar src={player?.photoUrl} alt={transfer.playerName} sx={{ width: 32, height: 32 }} />
                                        {transfer.playerName}
                                        <SidelinedIcon status={sidelinedMap?.get(String(transfer.playerId)) || null} />
                                    </Box>
                                </TableCell>
                                <TableCell>{(player && POSITION_MAP[player.position]) || player?.position || 'N/A'}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {player?.clubLogo && <Avatar src={player.clubLogo} alt={player?.clubName} sx={{ width: 24, height: 24, objectFit: 'contain' }} />}
                                        {player?.clubName || 'N/A'}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">€{transfer.amount}</TableCell>
                                <TableCell>{teamName}</TableCell>
                                <TableCell>
                                    <Chip label={getPhaseLabel(transfer.type)} size="small" />
                                </TableCell>
                                <TableCell>
                                    {statusChip}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};


function TransferMarket() {
    const { user } = useAuth();
    const { auctionId, auction: contextAuction } = useAuction();
    const notify = useNotification();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [tab, setTab] = useState(0);
    const [transfers, setTransfers] = useState([]);
    const [allPlayers, setAllPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [participantsMap, setParticipantsMap] = useState(new Map());
    const [playersMap, setPlayersMap] = useState(new Map());
    const [seasonClubIds, setSeasonClubIds] = useState(new Set());
    const [auction, setAuction] = useState(null);
    const sidelinedMap = useSidelinedMap(auction?.season);
    const [participantDetails, setParticipantDetails] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [transferSearchTerm, setTransferSearchTerm] = useState('');
    const [clubFilter, setClubFilter] = useState('');
    const [positionFilter, setPositionFilter] = useState('');
    const [sortBy, setSortBy] = useState('club');
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    // Per-seizoen puntentotalen voor het sorteren. Lazy geladen: pas ophalen zodra er
    // op punten gesorteerd wordt (null = nog niet geladen).
    const [prevSeasonId, setPrevSeasonId] = useState(null);
    const [currentSeasonTotals, setCurrentSeasonTotals] = useState(null);
    const [prevSeasonTotals, setPrevSeasonTotals] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [playerToAcquire, setPlayerToAcquire] = useState(null);
    const [myTeamPlayerIds, setMyTeamPlayerIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [transferPage, setTransferPage] = useState(1);
    const [myTransferPage, setMyTransferPage] = useState(1);
    const PAGE_SIZE = 50;

    const [isBidModalOpen, setIsBidModalOpen] = useState(false);
    const [playerToBidOn, setPlayerToBidOn] = useState(null);
    const [activeBlindPoolRound, setActiveBlindPoolRound] = useState(null);
    const [myBids, setMyBids] = useState([]);
    const [maxBidAmount, setMaxBidAmount] = useState(0);
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [infoDialogMessage, setInfoDialogMessage] = useState('');
    const [scoutingList, setScoutingList] = useState([]);
    const [filterScouted, setFilterScouted] = useState(false);
    const [isRoundRunning, setIsRoundRunning] = useState(false);
    const [transfersModalOpen, setTransfersModalOpen] = useState(false);
    const [detailPlayer, setDetailPlayer] = useState(null);
    const [activeTransferRound, setActiveTransferRound] = useState(null);

    // Draft state
    const [draftConfig, setDraftConfig] = useState(null);
    const [draftOfferings, setDraftOfferings] = useState([]);
    const [isDraftConfirmOpen, setIsDraftConfirmOpen] = useState(false);
    const [draftPlayerToAcquire, setDraftPlayerToAcquire] = useState(null);
    const [draftSearchTerm, setDraftSearchTerm] = useState('');
    const [draftClubFilter, setDraftClubFilter] = useState('');
    const [draftPositionFilter, setDraftPositionFilter] = useState('');

    useEffect(() => {
        if (!auctionId || !user) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const unsubAuction = onSnapshot(doc(db, 'auctions', auctionId), (docSnap) => {
            if (docSnap.exists()) {
                setAuction(docSnap.data());
            } else {
                setError("Veiling niet gevonden.");
                setAuction(null);
            }
        }, (err) => {
            setError("Fout bij het ophalen van veilingdata.");
            console.error(err);
        });

        const unsubParticipant = onSnapshot(doc(db, 'auctions', auctionId, 'participants', user.uid), (docSnap) => {
            if (docSnap.exists()) {
                setParticipantDetails(docSnap.data());
            }
        });

        const unsubTransfers = onSnapshot(query(collection(db, 'auctions', auctionId, 'auctionTransfers'), orderBy('timestamp', 'asc')), (snapshot) => {
            const allTransferDocs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            // Update transfers state real-time zodat berekende transfersLeft altijd actueel is
            setTransfers(allTransferDocs);

            const myPlayersMap = new Map();
            allTransferDocs.forEach(transfer => {
                if (transfer.to === user.uid) {
                    myPlayersMap.set(transfer.playerId, true);
                }
                if (transfer.from === user.uid && transfer.to !== user.uid) {
                    myPlayersMap.delete(transfer.playerId);
                }
            });
            const teamIds = Array.from(myPlayersMap.keys());
            console.log('Debug TransferMarket (Snapshot):', { 
                teamSize: teamIds.length, 
                teamIds 
            });
            setMyTeamPlayerIds(teamIds);
        });

        const blindPoolRoundsQuery = query(
            collection(db, 'auctions', auctionId, 'blindPoolRounds'),
            where('status', '==', 'open'),
            limit(1)
        );

        const unsubBlindPoolRounds = onSnapshot(blindPoolRoundsQuery, (snapshot) => {
            if (!snapshot.empty) {
                const round = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
                setActiveBlindPoolRound(round);
            } else {
                setActiveBlindPoolRound(null);
            }
        });

        const unsubScoutingList = onSnapshot(doc(db, 'auctions', auctionId, 'auctionScoutinglist', user.uid), (docSnap) => {
            if (docSnap.exists()) {
                setScoutingList(docSnap.data().scoutedPlayers || []);
            } else {
                setScoutingList([]);
            }
        });

        const scheduleQuery = query(collection(db, 'auctions', auctionId, 'schedule'));
        const unsubSchedule = onSnapshot(scheduleQuery, (snapshot) => {
            const isAnyRoundRunning = snapshot.docs.some(doc => doc.data().status === 'running');
            setIsRoundRunning(isAnyRoundRunning);
        });

        // Draft config listener
        const unsubDraftConfig = onSnapshot(doc(db, 'auctions', auctionId, 'draft', 'config'), (docSnap) => {
            if (docSnap.exists()) {
                setDraftConfig(docSnap.data());
            } else {
                setDraftConfig(null);
            }
        });

        // Draft offerings listener
        const unsubDraftOfferings = onSnapshot(collection(db, 'auctions', auctionId, 'draft', 'config', 'offerings'), (snapshot) => {
            setDraftOfferings(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // Listener voor actieve transferronde
        const transferRoundsQuery = query(
            collection(db, 'auctions', auctionId, 'transferRounds'),
            where('status', '==', 'open')
        );
        const unsubTransferRounds = onSnapshot(transferRoundsQuery, (snapshot) => {
            if (!snapshot.empty) {
                setActiveTransferRound({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
            } else {
                setActiveTransferRound(null);
            }
        });

        return () => {
            unsubAuction();
            unsubParticipant();
            unsubTransfers();
            unsubBlindPoolRounds();
            unsubScoutingList();
            unsubSchedule();
            unsubDraftConfig();
            unsubDraftOfferings();
            unsubTransferRounds();
        };
    }, [auctionId, user]);

    useEffect(() => {
        if (!activeBlindPoolRound?.id || !user?.uid || !auctionId) {
            setMyBids([]);
            return;
        }

        const myBidsQuery = query(
            collection(db, 'auctions', auctionId, 'blindPoolRounds', activeBlindPoolRound.id, 'bids'),
            where('participantId', '==', user.uid)
        );

        const unsubscribe = onSnapshot(myBidsQuery, (bidsSnapshot) => {
            setMyBids(bidsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => unsubscribe();

    }, [activeBlindPoolRound, user, auctionId]);

    useEffect(() => {
        if (!auction?.season) return;

        const fetchOtherData = async () => {
            setLoading(true);
            setError(null);
            try {
                const participantsRef = collection(db, 'auctions', auctionId, 'participants');

                const [playersSnap, participantsSnap, clubsSnap] = await Promise.all([
                    getDocs(seasonPlayersCollection(auction.season)),
                    getDocs(participantsRef),
                    getDocs(seasonClubsCollection(auction.season)),
                ]);

                setSeasonClubIds(new Set(clubsSnap.docs.map(doc => Number(doc.data().id))));

                const fetchedPlayers = playersSnap.docs.map(doc => {
                    const player = doc.data();
                    return {
                        ...player,
                        id: doc.id
                    };
                });
                const sortedPlayers = sortPlayers(fetchedPlayers);
                setAllPlayers(sortedPlayers);

                const newPlayersMap = new Map();
                fetchedPlayers.forEach(p => newPlayersMap.set(String(p.id), p));
                setPlayersMap(newPlayersMap);

                const newParticipantsMap = new Map();
                participantsSnap.docs.forEach(doc => newParticipantsMap.set(doc.data().uid, doc.data().name));
                setParticipantsMap(newParticipantsMap);

            } catch (err) {
                setError('Fout bij het ophalen van de transfermarkt data.');
                console.error(err);
            }
            setLoading(false);
        };

        fetchOtherData();
    }, [auction, auctionId]);

    const provisionalTeam = useMemo(() => {
        const myCurrentTeamPlayers = myTeamPlayerIds.map(id => playersMap.get(String(id))).filter(Boolean);
        const myBidPlayers = myBids.map(bid => playersMap.get(String(bid.playerId))).filter(Boolean);
        const combined = [...myCurrentTeamPlayers, ...myBidPlayers];
        const playerMap = new Map();
        combined.forEach(p => playerMap.set(p.id, p));
        return Array.from(playerMap.values());
    }, [myTeamPlayerIds, myBids, playersMap]);

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const handleAcquirePlayer = (player) => {
        setPlayerToAcquire(player);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setPlayerToAcquire(null);
    };

    const handleConfirmTransfer = async (playerToReplaceId) => {
        if (!playerToAcquire) return;

        if (!playerToReplaceId) {
            const error = validateTeamComposition(myTeamPlayers, playerToAcquire);
            if (error) {
                setInfoDialogMessage(error);
                setInfoDialogOpen(true);
                return;
            }
        }

        const executeTransfer = httpsCallable(functions, 'executeTransfer');

        try {
            await executeTransfer({
                auctionId: auctionId,
                playerToAcquireId: playerToAcquire.id,
                playerToReplaceId: playerToReplaceId || null,
            });
            handleCloseModal();
            setIsSuccessModalOpen(true);
        } catch (error) {
            console.error("Fout bij het verwerken van de transfer:", error);
            setError(`Er is iets misgegaan bij de transfer: ${error.message}`);
        }
    };

    const handleCloseSuccessModalAndNavigate = () => {
        setIsSuccessModalOpen(false);
        navigate('../my-team');
    };

    const handleOpenBidModal = (player) => {
        if (!activeBlindPoolRound) {
            setInfoDialogMessage("Er loopt op dit moment geen Blinde Pool ronde.");
            setInfoDialogOpen(true);
            return;
        }

        if (!participantDetails) return;

        if ((myBids.length) >= computedTransfersLeft) {
            setInfoDialogMessage('Je hebt het maximale aantal biedingen voor je resterende transfers al geplaatst.');
            setInfoDialogOpen(true);
            return;
        }
        const alreadyBid = myBids.some(bid => bid.playerId === player.id);
        if (alreadyBid) {
            setInfoDialogMessage('Je hebt al een bod op deze speler geplaatst.');
            setInfoDialogOpen(true);
            return;
        }

        const validationError = validateTeamComposition(provisionalTeam, player);
        if (validationError) {
            setInfoDialogMessage(validationError);
            setInfoDialogOpen(true);
            return;
        }

        const totalBidAmount = myBids.reduce((sum, bid) => sum + bid.amount, 0);
        const teamSize = 12;
        const emptySlots = teamSize - provisionalTeam.length;
        const slotsToFillAfterCurrentBid = emptySlots - 1;
        const minimumBudgetToReserve = Math.max(0, slotsToFillAfterCurrentBid);
        const maxAllowed = participantDetails.budget - totalBidAmount - minimumBudgetToReserve;

        setMaxBidAmount(maxAllowed);
        setPlayerToBidOn(player);
        setIsBidModalOpen(true);
    };

    const handleCloseBidModal = () => {
        setPlayerToBidOn(null);
        setIsBidModalOpen(false);
    };

    const handleConfirmBid = async (player, amount) => {
        if (!activeBlindPoolRound) {
            setInfoDialogMessage('Geen actieve blinde pool ronde gevonden.');
            setInfoDialogOpen(true);
            return;
        }
        setLoading(true);
        try {
            const bidsCollectionRef = collection(db, 'auctions', auctionId, 'blindPoolRounds', activeBlindPoolRound.id, 'bids');
            await addDoc(bidsCollectionRef, {
                participantId: user.uid,
                participantName: participantDetails.name || user.displayName || user.email,
                playerId: player.id,
                playerName: player.name,
                playerClub: player.clubName,
                playerClubLogo: player.clubLogo || null,
                playerPosition: player.position,
                amount: Number(amount),
                timestamp: serverTimestamp()
            });
        } catch (err) {
            console.error("Error placing blind pool bid:", err);
            setError("Fout bij het plaatsen van het bod.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleScoutPlayer = async (playerId) => {
        if (!user || !auctionId) return;
        const isScouted = scoutingList.includes(playerId);
        const spelfase = contextAuction?.spelfase;
        // Tijdens de Veiling ligt de scoutlijst vast: niets meer toevoegen of verwijderen.
        if (isScoutingLocked(spelfase)) {
            notify.warning('Tijdens de veiling kun je je scoutlijst niet meer wijzigen.');
            return;
        }
        // In de Voorbereiding mag een deelnemer maximaal MAX_SCOUTED_PLAYERS spelers scouten.
        const limit = scoutingLimit(spelfase);
        if (!isScouted && limit !== null && scoutingList.length >= limit) {
            notify.warning(`Je mag maximaal ${limit} spelers scouten. Verwijder eerst een gescoute speler.`);
            return;
        }
        const docRef = doc(db, 'auctions', auctionId, 'auctionScoutinglist', user.uid);
        try {
            if (isScouted) {
                await updateDoc(docRef, { scoutedPlayers: arrayRemove(playerId) });
            } else {
                await setDoc(docRef, { scoutedPlayers: arrayUnion(playerId) }, { merge: true });
            }
        } catch (err) {
            console.error("Error updating scouting list:", err);
            setError("Fout bij het bijwerken van de scoutinglijst.");
        }
    };

    const myTeamPlayers = useMemo(() => {
        return myTeamPlayerIds.map(id => playersMap.get(String(id))).filter(Boolean);
    }, [myTeamPlayerIds, playersMap]);

    const replaceablePlayers = useMemo(() => {
        if (!playerToAcquire) return [];

        const teamComposition = myTeamPlayers.reduce((acc, p) => {
            acc[p.position] = (acc[p.position] || 0) + 1;
            return acc;
        }, {});

        const positionLimits = {
            Goalkeeper: { min: 1, max: 1 },
            Defender: { min: 3, max: 5 },
            Midfielder: { min: 3, max: 5 },
            Attacker: { min: 2, max: 4 },
            Coach: { min: 1, max: 1 },
        };

        return myTeamPlayers.filter(playerToReplace => {
            const newPos = playerToAcquire.position;
            const oldPos = playerToReplace.position;

            if (newPos === oldPos) {
                return true; // Swapping same position is always fine
            }

            // If we remove the player, does the old position fall below the minimum?
            if ((teamComposition[oldPos] || 0) - 1 < positionLimits[oldPos]?.min) {
                return false;
            }

            // If we add the new player, does the new position exceed the maximum?
            if ((teamComposition[newPos] || 0) + 1 > positionLimits[newPos]?.max) {
                return false;
            }

            return true;
        });
    }, [playerToAcquire, myTeamPlayers]);

    // Bouw playerOwnerMap: welke speler hoort bij welke deelnemer
    const playerOwnerMap = useMemo(() => {
        const chronologicalTransfers = [...transfers].sort((a, b) => {
            const timeA = a.timestamp?.seconds || 0;
            const timeB = b.timestamp?.seconds || 0;
            return timeA - timeB;
        });
        const ownerMap = new Map();
        chronologicalTransfers.forEach(transfer => {
            ownerMap.set(String(transfer.playerId), transfer.to);
        });
        return ownerMap;
    }, [transfers]);

    // Bereken owned player IDs op basis van playerOwnerMap (real-time via transfers snapshot)
    const ownedPlayerIds = useMemo(() => {
        const ownedIds = new Set();
        for (const [playerId, ownerId] of playerOwnerMap.entries()) {
            if (participantsMap.has(ownerId)) {
                ownedIds.add(playerId);
            }
        }
        return ownedIds;
    }, [playerOwnerMap, participantsMap]);

    // Alleen spelers van een deelnemende seizoensclub horen op de transfermarkt.
    // Dit verbergt zowel op Non-CL gezette spelers als spelers die naar een andere
    // niet-deelnemende club zijn vertrokken.
    const freeAgents = useMemo(
        () => allPlayers.filter(p => !ownedPlayerIds.has(String(p.id)) && isActiveSeasonPlayer(p, seasonClubIds)),
        [allPlayers, ownedPlayerIds, seasonClubIds]
    );

    const clubs = useMemo(() => {
        const clubNames = [...new Set(allPlayers.filter(p => isActiveSeasonPlayer(p, seasonClubIds)).map(p => p.clubName).filter(Boolean))];
        return clubNames.sort();
    }, [allPlayers, seasonClubIds]);

    const positions = useMemo(() => {
        const positionNames = [...new Set(allPlayers.map(p => p.position).filter(Boolean))];
        return positionNames.sort();
    }, [allPlayers]);

    const translatedPositions = useMemo(() => {
        return positions.map(p => ({ key: p, value: POSITION_MAP[p] || p })).sort((a, b) => a.value.localeCompare(b.value));
    }, [positions]);

    // Actieve seizoensspelers (stabiele lijst, verandert niet bij elke transfer-snapshot),
    // gebruikt als input voor het ophalen van de puntentotalen.
    const activePlayerIds = useMemo(
        () => allPlayers.filter(p => isActiveSeasonPlayer(p, seasonClubIds)).map(p => String(p.id)),
        [allPlayers, seasonClubIds]
    );

    const seasonPlayerTotalsCall = useMemo(() => httpsCallable(functions, 'getSeasonPlayerTotals'), []);

    // Bepaal het vorige seizoen (meest recent afgerond) — zelfde logica als de
    // veiling-projectiekaart. Nodig om de punten van vorig seizoen op te halen.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const seasons = await fetchSeasons(); // nieuwste eerst
                const vorige = seasons.find(s => s.status === 'afgerond');
                if (!cancelled) setPrevSeasonId(vorige ? String(vorige.id) : null);
            } catch (err) {
                console.error('Kon het vorige seizoen niet bepalen:', err);
                if (!cancelled) setPrevSeasonId(null);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Puntentotalen huidig seizoen ophalen zodra daarop gesorteerd wordt.
    useEffect(() => {
        if (sortBy !== 'currentPoints' || currentSeasonTotals !== null) return;
        if (!auction?.season || activePlayerIds.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await seasonPlayerTotalsCall({ playerIds: activePlayerIds, seasonId: String(auction.season) });
                if (!cancelled) setCurrentSeasonTotals(res.data || {});
            } catch (err) {
                console.error('Kon puntentotalen huidig seizoen niet ophalen:', err);
                if (!cancelled) setCurrentSeasonTotals({});
            }
        })();
        return () => { cancelled = true; };
    }, [sortBy, currentSeasonTotals, auction?.season, activePlayerIds, seasonPlayerTotalsCall]);

    // Puntentotalen vorig seizoen ophalen zodra daarop gesorteerd wordt.
    useEffect(() => {
        if (sortBy !== 'prevPoints' || prevSeasonTotals !== null) return;
        if (!prevSeasonId || activePlayerIds.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await seasonPlayerTotalsCall({ playerIds: activePlayerIds, seasonId: prevSeasonId });
                if (!cancelled) setPrevSeasonTotals(res.data || {});
            } catch (err) {
                console.error('Kon puntentotalen vorig seizoen niet ophalen:', err);
                if (!cancelled) setPrevSeasonTotals({});
            }
        })();
        return () => { cancelled = true; };
    }, [sortBy, prevSeasonTotals, prevSeasonId, activePlayerIds, seasonPlayerTotalsCall]);

    const filteredFreeAgents = useMemo(() => {
        let players = freeAgents;
        if (filterScouted) {
            players = players.filter(p => scoutingList.includes(p.id));
        }
        if (searchTerm) {
            players = players.filter(p =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (clubFilter) {
            players = players.filter(p => p.clubName === clubFilter);
        }
        if (positionFilter) {
            players = players.filter(p => p.position === positionFilter);
        }
        // Sorteren. 'club' laat de binnengehaalde volgorde (club → positie → naam) staan;
        // de puntenopties sorteren aflopend op het puntentotaal van het gekozen seizoen.
        if (sortBy === 'currentPoints' || sortBy === 'prevPoints') {
            const totals = (sortBy === 'currentPoints' ? currentSeasonTotals : prevSeasonTotals) || {};
            players = [...players].sort((a, b) => {
                const pa = totals[String(a.id)]?.totalPoints || 0;
                const pb = totals[String(b.id)]?.totalPoints || 0;
                if (pb !== pa) return pb - pa;
                return (a.name || '').localeCompare(b.name || '');
            });
        }
        return players;
    }, [freeAgents, searchTerm, clubFilter, positionFilter, filterScouted, scoutingList, sortBy, currentSeasonTotals, prevSeasonTotals]);

    const visibleFreeAgents = useMemo(() => {
        return filteredFreeAgents.slice(0, currentPage * PAGE_SIZE);
    }, [filteredFreeAgents, currentPage]);

    const freeAgentsValidationMessages = useMemo(() => {
        const messages = new Map();
        visibleFreeAgents.forEach(player => {
            const error = validateTeamComposition(provisionalTeam, player);
            if (error) {
                messages.set(player.id, error);
            }
        });
        return messages;
    }, [provisionalTeam, visibleFreeAgents]);

    const observer = useRef();
    const lastPlayerElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleFreeAgents.length < filteredFreeAgents.length) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, filteredFreeAgents.length, visibleFreeAgents.length]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, clubFilter, positionFilter, filterScouted, sortBy]);


    const myTransfers = useMemo(() => {
        if (!user) return [];
        return transfers.filter(t => t.to === user.uid || t.from === user.uid);
    }, [transfers, user]);

    // Bereken transfersLeft op basis van werkelijke transfers (zelfde logica als AdminOverview)
    const computedTransfersLeft = useMemo(() => {
        if (!user || !auction?.spelfase) return 0;

        const getTotalAllowed = (phase) => {
            const preAuction = ['Voorbereiding', 'Veiling', 'Blinde Pool'];
            const preKnockout = ['Poulefase', 'Tussenronde', 'Draft'];
            const knockout = ['Knock-out', 'Finaleronde'];
            if (preAuction.includes(phase)) return 12;
            if (preKnockout.includes(phase)) return 15;
            if (knockout.includes(phase)) return 19;
            return 0;
        };

        const totalAllowed = getTotalAllowed(auction.spelfase);
        const incomingTransfers = transfers.filter(t => t.to === user.uid);

        // Sluit coach-change en Draft transfers uit (initiële teamopbouw)
        const excludedTypes = ['coach-change', 'Draft'];
        const transfersUsed = incomingTransfers.filter(t => !excludedTypes.includes(t.type)).length;
        const nonClTransfersCount = transfers.filter(t => t.from === user.uid && REFUNDED_TRANSFER_TYPES.includes(t.type)).length;

        return totalAllowed - transfersUsed + nonClTransfersCount;
    }, [user, transfers, auction?.spelfase]);

    // Sorteer transfers: aflopend op timestamp, dan op deelnemer naam, dan Inkomend boven Uitgaand
    const sortTransfers = useCallback((transfersList) => {
        return [...transfersList].sort((a, b) => {
            // 1. Primair: timestamp aflopend
            const timeA = a.timestamp?.seconds || 0;
            const timeB = b.timestamp?.seconds || 0;
            if (timeA !== timeB) return timeB - timeA;

            // 2. Secundair: deelnemer naam oplopend
            const teamNameA = participantsMap.get(a.to) || participantsMap.get(a.from) || '';
            const teamNameB = participantsMap.get(b.to) || participantsMap.get(b.from) || '';
            const nameCompare = teamNameA.localeCompare(teamNameB, 'nl-NL');
            if (nameCompare !== 0) return nameCompare;

            // 3. Tertiair: Inkomend (0) boven Uitgaand (1)
            const statusA = participantsMap.has(a.to) ? 0 : 1;
            const statusB = participantsMap.has(b.to) ? 0 : 1;
            return statusA - statusB;
        });
    }, [participantsMap]);

    const filteredTransfers = useMemo(() => {
        const base = !transferSearchTerm ? transfers : transfers.filter(t => {
            const searchLower = transferSearchTerm.toLowerCase();
            const playerMatch = t.playerName.toLowerCase().includes(searchLower);
            const fromTeamName = participantsMap.get(t.from) || '';
            const toTeamName = participantsMap.get(t.to) || '';
            const teamMatch = fromTeamName.toLowerCase().includes(searchLower) || 
                              toTeamName.toLowerCase().includes(searchLower);
            return playerMatch || teamMatch;
        });
        return sortTransfers(base);
    }, [transfers, transferSearchTerm, participantsMap, sortTransfers]);

    const filteredMyTransfers = useMemo(() => {
        const base = !transferSearchTerm ? myTransfers : myTransfers.filter(t =>
            t.playerName.toLowerCase().includes(transferSearchTerm.toLowerCase())
        );
        return sortTransfers(base);
    }, [myTransfers, transferSearchTerm, sortTransfers]);

    const visibleTransfers = useMemo(() => {
        return filteredTransfers.slice(0, transferPage * PAGE_SIZE);
    }, [filteredTransfers, transferPage]);

    const visibleMyTransfers = useMemo(() => {
        return filteredMyTransfers.slice(0, myTransferPage * PAGE_SIZE);
    }, [filteredMyTransfers, myTransferPage]);

    // --- Draft computed values ---
    const isDraftPhase = auction?.spelfase === 'Draft';
    const isDraftActive = isDraftPhase && draftConfig?.status === 'active';
    const isDraftLotteryDone = isDraftPhase && draftConfig?.draftOrder?.length > 0;
    // Draft spelers tab: zichtbaar voor alle deelnemers gedurende de hele Draft spelfase
    const showDraftPlayersTab = isDraftPhase && !!draftConfig;
    // Draft board tab: zichtbaar voor alle deelnemers zodra de loting is gedaan
    const showDraftBoardTab = isDraftLotteryDone;

    const isMyDraftTurn = useMemo(() => {
        if (!isDraftActive || !draftConfig?.draftOrder) return false;
        const currentPickIndex = draftConfig.currentPickIndex || 0;
        return draftConfig.draftOrder[currentPickIndex] === user?.uid;
    }, [isDraftActive, draftConfig, user]);

    const allDraftTransfers = useMemo(() => {
        return transfers.filter(t => t.type === 'Draft');
    }, [transfers]);

    const myDraftPickClubs = useMemo(() => {
        const clubs = new Set();
        allDraftTransfers
            .filter(t => t.to === user?.uid)
            .forEach(t => { if (t.playerClubName) clubs.add(t.playerClubName); });
        return clubs;
    }, [allDraftTransfers, user]);

    // Beschikbare spelers voor de draft: spelers van uitgeschakelde deelnemers + vrije spelers die nog niet gedraft zijn
    const draftAvailablePlayers = useMemo(() => {
        if (!isDraftPhase || !draftConfig) return [];
        const eliminatedUids = new Set(draftConfig.eliminatedParticipants || []);
        const draftedPlayerIds = new Set(allDraftTransfers.map(t => String(t.playerId)));

        return allPlayers.filter(player => {
            const playerId = String(player.id);
            // Al gekozen in de draft? Skip.
            if (draftedPlayerIds.has(playerId)) return false;
            // Naar een niet-deelnemende club vertrokken? Doet niet meer mee.
            if (!isActiveSeasonPlayer(player, seasonClubIds)) return false;

            const owner = playerOwnerMap.get(playerId);
            // Speler is vrij (geen eigenaar of eigenaar is geen deelnemer)
            const isFree = !owner || !participantsMap.has(owner);
            // Speler hoort bij uitgeschakelde deelnemer
            const isFromEliminated = owner && eliminatedUids.has(owner);

            return isFree || isFromEliminated;
        });
    }, [isDraftPhase, draftConfig, allPlayers, allDraftTransfers, playerOwnerMap, participantsMap, seasonClubIds]);

    // Gefilterde draft spelers
    const filteredDraftPlayers = useMemo(() => {
        let players = draftAvailablePlayers;
        if (draftSearchTerm) {
            players = players.filter(p => p.name.toLowerCase().includes(draftSearchTerm.toLowerCase()));
        }
        if (draftClubFilter) {
            players = players.filter(p => p.clubName === draftClubFilter);
        }
        if (draftPositionFilter) {
            players = players.filter(p => p.position === draftPositionFilter);
        }
        return players;
    }, [draftAvailablePlayers, draftSearchTerm, draftClubFilter, draftPositionFilter]);

    // Validatie berichten voor draft spelers (niet de team-vol check, want die wordt afgehandeld via vervanging)
    const isTeamFull = myTeamPlayers.length >= TEAM_RULES['Totaal'].max;
    const draftValidationMessages = useMemo(() => {
        const messages = new Map();
        if (!isTeamFull) {
            // Alleen compositie-checks als team niet vol is
            filteredDraftPlayers.forEach(player => {
                const error = validateTeamComposition(myTeamPlayers, player);
                if (error) messages.set(player.id, error);
            });
        }
        return messages;
    }, [myTeamPlayers, filteredDraftPlayers, isTeamFull]);

    // Vervangbare spelers voor draft (zelfde logica als bij gewone transfers)
    const draftReplaceablePlayers = useMemo(() => {
        if (!draftPlayerToAcquire || !isTeamFull) return [];

        const teamComposition = myTeamPlayers.reduce((acc, p) => {
            acc[p.position] = (acc[p.position] || 0) + 1;
            return acc;
        }, {});

        const positionLimits = {
            Goalkeeper: { min: 1, max: 1 },
            Defender: { min: 3, max: 5 },
            Midfielder: { min: 3, max: 5 },
            Attacker: { min: 2, max: 4 },
            Coach: { min: 1, max: 1 },
        };

        return myTeamPlayers.filter(playerToReplace => {
            const newPos = draftPlayerToAcquire.position;
            const oldPos = playerToReplace.position;

            if (newPos === oldPos) return true;

            if ((teamComposition[oldPos] || 0) - 1 < positionLimits[oldPos]?.min) return false;
            if ((teamComposition[newPos] || 0) + 1 > positionLimits[newPos]?.max) return false;

            return true;
        });
    }, [draftPlayerToAcquire, myTeamPlayers, isTeamFull]);

    // Draft offerings map: playerId → { profitPercentage, offeredByName }
    const draftOfferingsMap = useMemo(() => {
        const map = new Map();
        draftOfferings.forEach(offering => {
            map.set(String(offering.id), {
                profitPercentage: offering.profitPercentage || 0,
                offeredByName: participantsMap.get(offering.offeredByUid) || 'Onbekend',
            });
        });
        return map;
    }, [draftOfferings, participantsMap]);

    // Clubs beschikbaar in draft spelers (voor filter)
    const draftClubs = useMemo(() => {
        const clubNames = [...new Set(draftAvailablePlayers.map(p => p.clubName).filter(Boolean))];
        return clubNames.sort();
    }, [draftAvailablePlayers]);

    // Draft tab offset: hoeveel extra tabs staan er voor de bestaande tabs
    const draftTabOffset = (showDraftBoardTab ? 1 : 0) + (showDraftPlayersTab ? 1 : 0);

    const transferObserver = useRef();
    const lastTransferElementRef = useCallback(node => {
        if (loading) return;
        if (transferObserver.current) transferObserver.current.disconnect();
        transferObserver.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleTransfers.length < filteredTransfers.length) {
                setTransferPage(prevPage => prevPage + 1);
            }
        });
        if (node) transferObserver.current.observe(node);
    }, [loading, visibleTransfers.length, filteredTransfers.length]);

    const myTransferObserver = useRef();
    const lastMyTransferElementRef = useCallback(node => {
        if (loading) return;
        if (myTransferObserver.current) myTransferObserver.current.disconnect();
        myTransferObserver.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleMyTransfers.length < filteredMyTransfers.length) {
                setMyTransferPage(prevPage => prevPage + 1);
            }
        });
        if (node) myTransferObserver.current.observe(node);
    }, [loading, visibleMyTransfers.length, filteredMyTransfers.length]);

    useEffect(() => {
        setTransferPage(1);
        setMyTransferPage(1);
    }, [transferSearchTerm]);

    // --- Draft handlers ---
    const handleDraftAcquire = (player) => {
        setDraftPlayerToAcquire(player);
        setIsDraftConfirmOpen(true);
    };

    const handleConfirmDraftTransfer = async (playerToReplaceId) => {
        if (!draftPlayerToAcquire) return;

        // Als team vol is en er geen vervanging gekozen is, blokkeer
        if (isTeamFull && !playerToReplaceId) {
            setInfoDialogMessage('Je team is vol. Kies een speler om te vervangen.');
            setInfoDialogOpen(true);
            return;
        }

        setLoading(true);
        try {
            const executeDraftTransferFn = httpsCallable(functions, 'executeDraftTransfer');
            await executeDraftTransferFn({
                auctionId,
                playerToAcquireId: draftPlayerToAcquire.id,
                playerToReplaceId: playerToReplaceId || null,
            });
            setIsDraftConfirmOpen(false);
            setDraftPlayerToAcquire(null);
            setIsSuccessModalOpen(true);
        } catch (error) {
            console.error("Fout bij het verwerken van de draft keuze:", error);
            const message = error?.message || 'Er is iets misgegaan bij de draft keuze.';
            setInfoDialogMessage(message);
            setInfoDialogOpen(true);
            setIsDraftConfirmOpen(false);
        } finally {
            setLoading(false);
        }
    };

    // Afgeleide waarden voor de compacte filterbalk (mobiel) en het filterscherm.
    const activeFilterCount = (clubFilter ? 1 : 0) + (positionFilter ? 1 : 0) + (filterScouted ? 1 : 0);
    const positionLabel = translatedPositions.find(p => p.key === positionFilter)?.value || positionFilter;
    const sortLabel = SORT_OPTIONS.find(o => o.value === sortBy)?.label;
    const clearAllFilters = () => {
        setClubFilter('');
        setPositionFilter('');
        setFilterScouted(false);
        setSortBy('club');
    };
    // Bij sorteren op punten tonen we die punten op de spelerskaartjes/-rijen. Het label
    // en de bijbehorende totalen-map hangen af van het gekozen seizoen; leeg = niet tonen.
    const pointsSeasonLabel = sortBy === 'currentPoints' ? 'dit seizoen' : sortBy === 'prevPoints' ? 'vorig seizoen' : '';
    const pointsMap = sortBy === 'currentPoints' ? (currentSeasonTotals || {}) : sortBy === 'prevPoints' ? (prevSeasonTotals || {}) : {};

    if (loading && allPlayers.length === 0) {
        return <Box sx={page.loadingContainer}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>Transfermarkt</Typography>
                <Chip
                    icon={<SyncAltIcon />}
                    label={`${computedTransfersLeft} transfers`}
                    variant="outlined"
                    color="primary"
                />
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    aria-label="transfermarkt tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        minHeight: { xs: 40, sm: 48 },
                        '& .MuiTab-root': {
                            fontSize: { xs: '0.75rem', sm: '1rem' },
                            minWidth: { xs: 'auto', sm: 90 },
                            minHeight: { xs: 40, sm: 48 },
                            px: { xs: 1, sm: 2 },
                            py: { xs: 0.75, sm: 1.5 },
                        },
                    }}
                >
                    {showDraftBoardTab && <Tab label="Draft" />}
                    {showDraftPlayersTab && <Tab label="Draft spelers" />}
                    <Tab label="Vrije Spelers" />
                    <Tab label="Alle Transfers" />
                    <Tab label="Mijn Transfers" />
                </Tabs>
            </Box>

            {/* Draft Board tab */}
            {showDraftBoardTab && (
                <Box hidden={tab !== 0}>
                    <DraftBoard
                        draftConfig={draftConfig}
                        participantsMap={participantsMap}
                        draftTransfers={allDraftTransfers}
                        currentUserUid={user?.uid}
                        isMobile={isMobile}
                    />
                </Box>
            )}

            {/* Draft Spelers tab */}
            {showDraftPlayersTab && (
                <Box hidden={tab !== (showDraftBoardTab ? 1 : 0)}>
                    {isDraftPhase && !isDraftActive && draftConfig?.status !== 'completed' && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            De draft is nog niet gestart. Wacht tot de beheerder de draft start.
                        </Alert>
                    )}
                    {draftConfig?.status === 'completed' && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            De draft is afgerond!
                        </Alert>
                    )}
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label="Zoek op naam" variant="outlined" value={draftSearchTerm} onChange={(e) => setDraftSearchTerm(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    sx={{ minWidth: 200 }}
                                    options={draftClubs}
                                    getOptionLabel={(option) => option || ""}
                                    value={draftClubFilter || null}
                                    onChange={(event, newValue) => { setDraftClubFilter(newValue || ''); }}
                                    renderInput={(params) => <TextField {...params} label="Club" variant="outlined" />}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth variant="outlined" sx={{ minWidth: 200 }}>
                                    <InputLabel>Positie</InputLabel>
                                    <Select value={draftPositionFilter} onChange={(e) => setDraftPositionFilter(e.target.value)} label="Positie">
                                        <MenuItem value=""><em>Alle posities</em></MenuItem>
                                        {translatedPositions.map(pos => (<MenuItem key={pos.key} value={pos.key}>{pos.value}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>
                    <DraftPlayersTable
                        players={filteredDraftPlayers}
                        onAcquire={handleDraftAcquire}
                        isMyTurn={isMyDraftTurn}
                        draftPickClubs={myDraftPickClubs}
                        validationMessages={draftValidationMessages}
                        offeringsMap={draftOfferingsMap}
                        participantsMap={participantsMap}
                        playerOwnerMap={playerOwnerMap}
                        isMobile={isMobile}
                        theme={theme}
                        isDraftActive={isDraftActive}
                        sidelinedMap={sidelinedMap}
                    />
                </Box>
            )}

            <Box hidden={tab !== (0 + draftTabOffset)}>
                {isDraftPhase && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Tijdens de draft fase kun je geen reguliere transfers doen. Gebruik het "Draft spelers" tabblad om spelers te kiezen.
                    </Alert>
                )}
                {activeTransferRound && activeTransferRound.participants?.[user?.uid] && (() => {
                    const trData = activeTransferRound.participants[user.uid];
                    const trDone = Math.max(0, trData.transfersLeftAtStart - computedTransfersLeft);
                    const trRemaining = Math.max(0, trData.allowance - trDone);
                    return (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Transferronde actief</strong> ({activeTransferRound.matchName}) — Je mag nog {trRemaining} transfer{trRemaining !== 1 ? 's' : ''} doen in deze ronde.
                        </Alert>
                    );
                })()}
                {activeTransferRound && !activeTransferRound.participants?.[user?.uid] && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Transfermarkt gesloten</strong> — Er is een transferronde actief voor andere deelnemers. Je kunt momenteel geen transfers doen.
                    </Alert>
                )}
                {isMobile ? (
                    /* Compacte mobiele filterbalk: één zoekveld + filterknop met teller.
                       Alle filters en het sorteren zitten in het uitschuivende filterscherm. */
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                            <CompactSearchField
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Zoek op naam"
                            />
                            <Badge badgeContent={activeFilterCount} color="primary" overlap="rectangular">
                                <IconButton
                                    aria-label="Filters en sorteren"
                                    onClick={() => setFilterSheetOpen(true)}
                                    sx={{
                                        height: '100%',
                                        border: '1px solid',
                                        borderColor: activeFilterCount > 0 ? 'primary.main' : 'rgba(255, 255, 255, 0.23)',
                                        borderRadius: 1,
                                        color: activeFilterCount > 0 ? 'primary.main' : 'text.secondary',
                                    }}
                                >
                                    <TuneIcon />
                                </IconButton>
                            </Badge>
                        </Box>
                        {(activeFilterCount > 0 || sortBy !== 'club') && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                {clubFilter && (
                                    <Chip label={clubFilter} onDelete={() => setClubFilter('')} size="small" color="primary" variant="outlined" />
                                )}
                                {positionFilter && (
                                    <Chip label={positionLabel} onDelete={() => setPositionFilter('')} size="small" color="secondary" variant="outlined" />
                                )}
                                {filterScouted && (
                                    <Chip icon={<StarIcon />} label="Gescout" onDelete={() => setFilterScouted(false)} size="small" color="primary" variant="outlined" />
                                )}
                                {sortBy !== 'club' && (
                                    <Chip icon={<SortIcon />} label={sortLabel} onDelete={() => setSortBy('club')} size="small" variant="outlined" />
                                )}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <TextField fullWidth label="Zoek op naam" variant="outlined" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    sx={{ minWidth: 180 }}
                                    options={clubs}
                                    getOptionLabel={(option) => option || ""}
                                    value={clubFilter || null}
                                    onChange={(event, newValue) => {
                                        setClubFilter(newValue || '');
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Club" variant="outlined" />}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>Positie</InputLabel>
                                    <Select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)} label="Positie">
                                        <MenuItem value=""><em>Alle posities</em></MenuItem>
                                        {translatedPositions.map(pos => (<MenuItem key={pos.key} value={pos.key}>{pos.value}</MenuItem>))
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>Sorteren</InputLabel>
                                    <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sorteren">
                                        {SORT_OPTIONS.map(o => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={filterScouted} onChange={(e) => setFilterScouted(e.target.checked)} />}
                                        label="Alleen gescout"
                                    />
                                    <Button
                                        onClick={() => setTransfersModalOpen(true)}
                                        variant="text"
                                        color="primary"
                                        size="small"
                                        startIcon={<SwapHorizIcon />}
                                    >
                                        Clubtransfers
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}
                <FreeAgentsTable
                    players={visibleFreeAgents}
                    onAcquire={handleAcquirePlayer}
                    onBid={handleOpenBidModal}
                    participantDetails={participantDetails}
                    auction={auction}
                    lastPlayerElementRef={lastPlayerElementRef}
                    myBids={myBids}
                    validationMessages={freeAgentsValidationMessages}
                    onToggleScout={handleToggleScoutPlayer}
                    scoutingList={scoutingList}
                    isRoundRunning={isRoundRunning}
                    isMobile={isMobile}
                    theme={theme}
                    activeTransferRound={activeTransferRound}
                    computedTransfersLeft={computedTransfersLeft}
                    sidelinedMap={sidelinedMap}
                    onPlayerClick={(p) => setDetailPlayer({ ...p, club: p.clubName })}
                    pointsMap={pointsMap}
                    pointsSeasonLabel={pointsSeasonLabel}
                />
                {visibleFreeAgents.length < filteredFreeAgents.length && (
                    <Box sx={page.loadingContainer}><CircularProgress /></Box>
                )}
                {filteredFreeAgents.length === 0 && !loading && (
                    <Typography sx={{ textAlign: 'center', p: 3 }}>Geen spelers gevonden die aan de criteria voldoen.</Typography>
                )}
            </Box >
            <Box hidden={tab !== (1 + draftTabOffset)}>
                <Box sx={{ mb: 2 }}>
                    <CompactSearchField value={transferSearchTerm} onChange={(e) => setTransferSearchTerm(e.target.value)} placeholder="Zoek op speler of team" />
                </Box>
                <TransfersTable
                    transfers={visibleTransfers}
                    participantsMap={participantsMap}
                    playersMap={playersMap}
                    isMobile={isMobile}
                    lastTransferElementRef={lastTransferElementRef}
                    sidelinedMap={sidelinedMap}
                />
                {visibleTransfers.length < filteredTransfers.length && (
                    <Box sx={page.loadingContainer}><CircularProgress /></Box>
                )}
            </Box>
            <Box hidden={tab !== (2 + draftTabOffset)}>
                <Box sx={{ mb: 2 }}>
                    <CompactSearchField value={transferSearchTerm} onChange={(e) => setTransferSearchTerm(e.target.value)} placeholder="Zoek op naam" />
                </Box>
                <TransfersTable
                    transfers={visibleMyTransfers}
                    participantsMap={participantsMap}
                    playersMap={playersMap}
                    isMobile={isMobile}
                    lastTransferElementRef={lastMyTransferElementRef}
                    sidelinedMap={sidelinedMap}
                />
                {visibleMyTransfers.length < filteredMyTransfers.length && (
                    <Box sx={page.loadingContainer}><CircularProgress /></Box>
                )}
            </Box>
            {console.log('Debug TransferMarket (Render):', { 
                myTeamPlayersLength: myTeamPlayers.length, 
                maxTeamSize: TEAM_RULES['Totaal'].max, 
                isTeamComplete: myTeamPlayers.length >= TEAM_RULES['Totaal'].max 
            })}
            <TransferConfirmationModal
                open={isModalOpen}
                onClose={handleCloseModal}
                playerToAcquire={playerToAcquire}
                replaceablePlayers={replaceablePlayers}
                onConfirm={handleConfirmTransfer}
                isTeamComplete={myTeamPlayers.length >= TEAM_RULES['Totaal'].max}
            />
            <Dialog
                open={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                aria-labelledby="transfer-success-title"
                aria-describedby="transfer-success-description"
            >
                <DialogTitle id="transfer-success-title">Transfer Geslaagd</DialogTitle>
                <DialogContent>
                    <DialogContentText id="transfer-success-description">
                        De transfer is geslaagd!
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSuccessModalAndNavigate} color="primary" autoFocus>
                        Naar Mijn Team
                    </Button>
                </DialogActions>
            </Dialog>
            <BlindPoolBidModal
                open={isBidModalOpen}
                onClose={handleCloseBidModal}
                player={playerToBidOn}
                onConfirm={handleConfirmBid}
                participantBudget={participantDetails?.budget}
                maxAllowedBid={maxBidAmount}
            />
            <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)}>
                <DialogTitle>Let op</DialogTitle>
                <DialogContent>
                    <DialogContentText>{infoDialogMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInfoDialogOpen(false)}>Sluiten</Button>
                </DialogActions>
            </Dialog>
            {/* Draft keuze bevestigings-dialoog */}
            <Dialog open={isDraftConfirmOpen} onClose={() => { setIsDraftConfirmOpen(false); setDraftPlayerToAcquire(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>Draft keuze bevestigen</DialogTitle>
                <DialogContent>
                    {draftPlayerToAcquire && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 1 }}>
                            <Avatar src={draftPlayerToAcquire.photoUrl} alt={draftPlayerToAcquire.name} sx={{ width: 56, height: 56 }} />
                            <Box>
                                <Typography variant="h6">{draftPlayerToAcquire.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {POSITION_MAP[draftPlayerToAcquire.position] || draftPlayerToAcquire.position} • {draftPlayerToAcquire.clubName}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {draftPlayerToAcquire && draftOfferingsMap.get(String(draftPlayerToAcquire.id)) && (
                        <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                            Deze speler is aangeboden met {draftOfferingsMap.get(String(draftPlayerToAcquire.id)).profitPercentage}% winstpercentage.
                        </Alert>
                    )}
                    {isTeamFull ? (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>
                                Je team is vol. Kies een speler om te vervangen:
                            </DialogContentText>
                            {draftReplaceablePlayers.length === 0 ? (
                                <Alert severity="warning">Geen spelers beschikbaar om te vervangen vanwege positieregels.</Alert>
                            ) : (
                                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Speler</TableCell>
                                                <TableCell>Positie</TableCell>
                                                <TableCell align="right">Actie</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {draftReplaceablePlayers.map(player => (
                                                <TableRow key={player.id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Avatar src={player.photoUrl} alt={player.name} sx={{ width: 28, height: 28 }} />
                                                            {player.name}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{POSITION_MAP[player.position] || player.position}</TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleConfirmDraftTransfer(player.id)}
                                                            disabled={loading}
                                                        >
                                                            Vervang
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    ) : (
                        <DialogContentText>
                            Weet je zeker dat je deze speler wilt kiezen? Deze keuze kan niet ongedaan worden gemaakt.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setIsDraftConfirmOpen(false); setDraftPlayerToAcquire(null); }}>Annuleren</Button>
                    {!isTeamFull && (
                        <Button onClick={() => handleConfirmDraftTransfer(null)} variant="contained" color="primary" disabled={loading}>
                            Bevestigen
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <TransfersModal
                open={transfersModalOpen}
                onClose={() => setTransfersModalOpen(false)}
                currentSeasonId={auction?.season}
            />
            <PlayerDetailModal
                open={!!detailPlayer}
                onClose={() => setDetailPlayer(null)}
                player={detailPlayer}
                auctionId={auctionId}
                seasonId={auction?.season}
                userId={user?.uid}
                showSeasonHistory
            />
            {/* Filterscherm (mobiel): zoeken staat in de balk, hier zitten sorteren en filters. */}
            <Drawer
                anchor="bottom"
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2, pb: 3, maxHeight: '88vh' } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Filters &amp; sorteren</Typography>
                    <Button
                        size="small"
                        color="inherit"
                        onClick={clearAllFilters}
                        disabled={activeFilterCount === 0 && sortBy === 'club'}
                        sx={{ color: 'text.secondary' }}
                    >
                        Wissen
                    </Button>
                </Box>

                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Sorteren op</Typography>
                <ToggleButtonGroup
                    orientation="vertical"
                    exclusive
                    fullWidth
                    value={sortBy}
                    onChange={(e, v) => { if (v) setSortBy(v); }}
                    sx={{ mb: 2.5, mt: 0.5, '& .MuiToggleButton-root': { justifyContent: 'flex-start', textTransform: 'none', py: 1 } }}
                >
                    {SORT_OPTIONS.map(o => (
                        <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Club</Typography>
                <Autocomplete
                    sx={{ mb: 2.5, mt: 0.5 }}
                    options={clubs}
                    getOptionLabel={(option) => option || ""}
                    value={clubFilter || null}
                    onChange={(event, newValue) => setClubFilter(newValue || '')}
                    renderInput={(params) => <TextField {...params} placeholder="Alle clubs" variant="outlined" size="small" />}
                    fullWidth
                />

                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Positie</Typography>
                <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2, mt: 0.5 }}>
                    <Select value={positionFilter} displayEmpty onChange={(e) => setPositionFilter(e.target.value)}>
                        <MenuItem value=""><em>Alle posities</em></MenuItem>
                        {translatedPositions.map(pos => (<MenuItem key={pos.key} value={pos.key}>{pos.value}</MenuItem>))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    sx={{ mb: 1, ml: 0, display: 'flex', justifyContent: 'space-between', width: '100%' }}
                    labelPlacement="start"
                    control={<Switch checked={filterScouted} onChange={(e) => setFilterScouted(e.target.checked)} />}
                    label="Alleen gescoute spelers"
                />

                <Divider sx={{ my: 1.5 }} />
                <Button
                    fullWidth
                    variant="text"
                    color="primary"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => { setFilterSheetOpen(false); setTransfersModalOpen(true); }}
                    sx={{ justifyContent: 'flex-start', mb: 2 }}
                >
                    Clubtransfers
                </Button>

                <Button fullWidth variant="contained" color="primary" size="large" onClick={() => setFilterSheetOpen(false)}>
                    Toon {filteredFreeAgents.length} spelers
                </Button>
            </Drawer>
        </Box>
    );
}

export default TransferMarket;